/**
 * CorrectionWoBadge (ticket-06)
 *
 * Visual indicator for Work Orders that are Correction WOs
 * (i.e., have parent_work_order_id set). Shows an orange/amber badge
 * with a link icon, and a tooltip identifying the originating work order.
 *
 * Also exports CorrectionToast — a one-shot toast notification shown
 * when MERGE creates a new Correction WO.
 */

import { useEffect, useState } from 'react'

// ─── CorrectionWoBadge ────────────────────────────────────────────────────────

interface CorrectionWoBadgeProps {
  parentWorkOrderId: string
  className?: string
}

export function CorrectionWoBadge({ parentWorkOrderId, className = '' }: CorrectionWoBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false)
  const short = parentWorkOrderId.slice(0, 8)

  return (
    <span className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        aria-label={`Наряд-корректировка. Оригинал: ${parentWorkOrderId}`}
        onClick={() => setShowTooltip((v) => !v)}
        onBlur={() => setShowTooltip(false)}
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium
                   bg-amber-500/15 text-amber-400 border border-amber-500/25
                   hover:bg-amber-500/25 transition-colors cursor-help"
      >
        {/* Wrench icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"
          aria-hidden="true" className="w-3 h-3">
          <path fillRule="evenodd"
            d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872l-.1-.34ZM8 10.93a2.929 2.929 0 1 1 0-5.858 2.929 2.929 0 0 1 0 5.858Z"
            clipRule="evenodd" />
        </svg>
        Корректировка
      </button>

      {showTooltip && (
        <div role="tooltip"
          className="absolute left-0 top-full mt-1 z-50 w-72 rounded border border-border
                     bg-bg-panel shadow-lg text-xs text-text-secondary p-3">
          <p className="font-semibold text-text-primary mb-1">Наряд-корректировка</p>
          <p>
            Создан автоматически из наряда{' '}
            <span className="font-mono text-amber-300">{short}…</span>
            {' '}при наличии пропущенных шагов.
          </p>
        </div>
      )}
    </span>
  )
}


// ─── CorrectionToast ──────────────────────────────────────────────────────────

interface CorrectionToastProps {
  correctionWorkOrderId: string
  /** Called when user navigates to the correction WO */
  onNavigate?: (id: string) => void
  /** Called when user dismisses */
  onDismiss?: () => void
  /** Auto-dismiss after ms (default 8000) */
  autoHideMs?: number
}

export function CorrectionToast({
  correctionWorkOrderId,
  onNavigate,
  onDismiss,
  autoHideMs = 8000,
}: CorrectionToastProps) {
  const [visible, setVisible] = useState(true)
  const short = correctionWorkOrderId.slice(0, 8)

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false)
      onDismiss?.()
    }, autoHideMs)
    return () => clearTimeout(timer)
  }, [autoHideMs, onDismiss])

  if (!visible) return null

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-50 flex items-start gap-3 rounded-lg border border-amber-500/30
                 bg-bg-panel shadow-2xl p-4 max-w-sm animate-in slide-in-from-bottom-4 duration-300"
    >
      {/* Icon */}
      <div className="shrink-0 w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
          className="w-4 h-4 text-amber-400" aria-hidden="true">
          <path fillRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
            clipRule="evenodd" />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary">Создан Наряд-корректировка</p>
        <p className="text-xs text-text-secondary mt-0.5">
          Пропущенные шаги перенесены в новый наряд{' '}
          <span className="font-mono text-amber-300">#{short}…</span>
        </p>
        {onNavigate && (
          <button
            onClick={() => { onNavigate(correctionWorkOrderId); setVisible(false) }}
            className="mt-2 text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2"
          >
            Открыть наряд →
          </button>
        )}
      </div>

      <button
        onClick={() => { setVisible(false); onDismiss?.() }}
        aria-label="Закрыть уведомление"
        className="shrink-0 text-text-secondary hover:text-text-primary transition-colors"
      >
        ✕
      </button>
    </div>
  )
}

export default CorrectionWoBadge
